import { useState } from "react";
import { Check, X, UserCircle2, MessageSquare, Pill, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { Link } from "react-router-dom";

const STEPS = [
  { id: "photo", icon: UserCircle2, label: "Set your profile photo", href: "/profile" },
  { id: "messages", icon: MessageSquare, label: "Review open conversations", href: "/provider?tab=messages" },
  { id: "refills", icon: Pill, label: "Check pending refill requests", href: "/provider?tab=refills" },
  { id: "assistant", icon: Sparkles, label: "Try the ADHD Education Assistant", href: "/resources" },
];

export default function ProviderChecklist() {
  const { user, checkAuth } = useAuth();
  const [dismissing, setDismissing] = useState(false);

  // Only show to regular providers who haven't dismissed it
  if (!user || user.role !== "admin" || user.is_super_admin || user.onboarding_checklist_dismissed) return null;

  const dismiss = async () => {
    setDismissing(true);
    try {
      await API.put("/auth/me", { onboarding_checklist_dismissed: true });
      await checkAuth();
    } catch { /* swallow — banner will reappear next time */ }
  };

  return (
    <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-4 animate-fade-in" data-testid="provider-onboarding-checklist">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <p className="text-sm font-semibold">Welcome, {user.full_name || "Provider"}</p>
          <p className="text-xs text-muted-foreground">A few quick things to get you productive:</p>
        </div>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={dismiss} disabled={dismissing} data-testid="dismiss-checklist-button" aria-label="Dismiss">
          <X className="w-4 h-4" />
        </Button>
      </div>
      <ul className="space-y-1.5 mt-3">
        {STEPS.map(({ id, icon: Icon, label, href }) => (
          <li key={id}>
            <Link to={href} className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/60 transition-colors text-sm group" data-testid={`checklist-step-${id}`}>
              <span className="w-6 h-6 rounded-full bg-white border border-primary/20 flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Icon className="w-3 h-3" />
              </span>
              <span className="flex-1">{label}</span>
              <Check className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
